import React, { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  ExperienceActionType,
  ExperienceDto,
  ExperienceListDto,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { CheckoutScriptDialog } from "@src/components/Dialogs/CheckoutScriptDialog";

import { useConfiguration } from "../../../../api/configurations/use-configuration";
import { useAllPersonalizationOverviewData } from "../../../../api/dashboards/dashboards";
import { usePauseExperience } from "../../../../api/experience-actions/pause-experience";
import { useStartExperience } from "../../../../api/experience-actions/start-experience";
import { useGetOrg } from "../../../../api/organizations/get-organization";
import { useIsAdmin } from "../../../../api/user-auths/is-admin";
import { useLoggedInUser } from "../../../../api/user-auths/login";
import {
  checkAnyScope,
  checkBooleanScope,
  checkSubscriptionStatus,
} from "../../../../components/ScopeWrappers/helpers";
import { selectedPersonalizationListFilters } from "../../../../store/slices/appSettings";
import { IAppState } from "../../../../store/types/store";
import { mpClickToggleCampaign } from "../../../../utils/mixpanel";
import {
  genericDescSort,
  genericSort,
  personalizationMetricSort,
  sortByStatus,
} from "../../../../utils/sortings";
import { PersonalizationOverviewCard } from "../../components/overview/PersonalizationOverviewCard";
import { PersonalizationOverviewHeader } from "../../components/PersonalizationOverviewHeader";
import {
  getPersonalizationTotalsFormattedData,
  isUsedInExperiment,
} from "../../utils";

import { MigrateFromCampaignsBanner } from "./MigrateFromCampaignsBanner";
import { NoPersonalizationsView } from "./NoPersonalizationsView";

interface PersonalizationOverviewBodyProps {
  personalizationsList: ExperienceListDto[];
  experienceList: ExperienceListDto[];
}

export const PersonalizationOverviewBody = ({
  personalizationsList,
  experienceList,
}: PersonalizationOverviewBodyProps) => {
  const organization = useGetOrg();
  const pauseExperience = usePauseExperience();
  const startExperience = useStartExperience();
  const user = useLoggedInUser();
  const viewedCampaignMigrateBanner = useSelector(
    (state: IAppState) => state.appSettings.viewedMigrateCampaign
  );

  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const personalizationTotalsData =
    useAllPersonalizationOverviewData("campaignTotals");

  const listFilters = useSelector(selectedPersonalizationListFilters);
  const [isSaving, setIsSaving] = useImmer<boolean[]>(
    personalizationsList.map(() => false)
  );

  const shopifyFunctionsEnabled = useConfiguration<boolean>(
    "shopify_functions_enabled"
  );
  const hasPriceModAccess = checkBooleanScope(
    "start_price_experiment",
    organization.data.org.configurations
  );
  const hasOfferModAccess = checkBooleanScope(
    "start_discount_experiment",
    organization.data.org.configurations
  );
  const isSubscriptionActive = checkSubscriptionStatus(true);
  const isAdmin = useIsAdmin();

  const sortedPersonalizationsList = useMemo(() => {
    let filteredAndSortedData: ExperienceListDto[] = personalizationsList;

    if (listFilters) {
      if (listFilters.search && listFilters.search.length > 0) {
        filteredAndSortedData = filteredAndSortedData.filter(
          (personalization) =>
            personalization.name
              .toLowerCase()
              .includes(listFilters.search!.toLowerCase()) ||
            personalization.id === listFilters.search
        );
      }
      if (listFilters.sortOptions === "createdAtTs") {
        filteredAndSortedData = filteredAndSortedData.sort((a, b) =>
          genericDescSort(a, b, "createdAtTs")
        );
      } else if (listFilters.sortOptions === "name") {
        filteredAndSortedData = filteredAndSortedData.sort((a, b) =>
          genericSort(a, b, "name")
        );
      } else if (listFilters.sortOptions === "status") {
        filteredAndSortedData = sortByStatus(
          filteredAndSortedData
        ) as ExperienceListDto[];
      }
      if (personalizationTotalsData.isSuccess) {
        if (listFilters.sortOptions === "orders") {
          filteredAndSortedData = personalizationMetricSort(
            filteredAndSortedData,
            personalizationTotalsData.data.metrics,
            "ORDERS"
          ) as ExperienceListDto[];
        } else if (listFilters.sortOptions === "visitors") {
          filteredAndSortedData = personalizationMetricSort(
            filteredAndSortedData,
            personalizationTotalsData.data.metrics,
            "VISITORS"
          ) as ExperienceListDto[];
        } else if (listFilters.sortOptions === "revenue") {
          filteredAndSortedData = personalizationMetricSort(
            filteredAndSortedData,
            personalizationTotalsData.data.metrics,
            "TOTAL_REVENUE"
          ) as ExperienceListDto[];
        }
      }
    }

    return filteredAndSortedData;
  }, [personalizationsList, listFilters, personalizationTotalsData?.data]);

  const handleEnableToggle = (
    campaign: ExperienceDto,
    campaignIndex: number
  ) => {
    const currentIsStarted = campaign.status === "started";
    const newValue = !currentIsStarted;
    if (campaignIndex >= 0) {
      setIsSaving((draft) => {
        draft[campaignIndex] = true;
        return draft;
      });

      const pathParams = {
        experienceId: campaign.id!,
        name: campaign.name,
        action: newValue
          ? ("start" as ExperienceActionType)
          : ("pause" as ExperienceActionType),
        pausedAtTs: null,
      };

      if (newValue) {
        startExperience.mutate(
          {
            pathParams,
            queryParams: undefined,
            body: {
              variationId: undefined,
              updatePricesToVariation: false,
            },
          },
          {
            onSettled: (data) => {
              setIsSaving((draft) => {
                draft[campaignIndex] = false;
                return draft;
              });
            },
            onSuccess: () => {
              mpClickToggleCampaign(
                campaign,
                user.isSuccess && user.data.user.id ? user.data.user.id : null,
                newValue
              );
            },
          }
        );
      } else {
        pauseExperience.mutate(
          {
            pathParams,
            queryParams: undefined,
            body: {
              variationId: undefined,
              updatePricesToVariation: false,
            },
          },
          {
            onSettled: (data) => {
              setIsSaving((draft) => {
                draft[campaignIndex] = false;
                return draft;
              });
            },
            onSuccess: () => {
              mpClickToggleCampaign(
                campaign,
                user.isSuccess && user.data.user.id ? user.data.user.id : null,
                newValue
              );
            },
          }
        );
      }
    }
  };

  return (
    <Grid container item direction={"column"}>
      {personalizationsList.length > 0 && (
        <Grid item>
          <PersonalizationOverviewHeader />
          <CheckoutScriptDialog />
        </Grid>
      )}

      {personalizationsList.length > 0 && !viewedCampaignMigrateBanner && (
        <Grid item mt={3}>
          <MigrateFromCampaignsBanner />
        </Grid>
      )}
      {personalizationsList.length > 0 ? (
        <Grid item container direction={"column"} rowSpacing={1} mt={"40px"}>
          {sortedPersonalizationsList.map((personalization, index) => {
            const personalizationData =
              personalizationTotalsData.data?.metrics.find(
                (metric) => metric["EXPERIENCE_ID"] === personalization.id
              );
            const formattedData: {
              visitors: string;
              orders: string;
              revenue: string;
            } = getPersonalizationTotalsFormattedData(
              personalizationData,
              storeCurrencyConfig!.value
            );
            const isUsed = isUsedInExperiment(
              experienceList,
              personalization.id
            );
            const shouldBlockPriceStart =
              personalization.testTypes.hasTestPricing && !hasPriceModAccess;
            const shouldBlockOfferStart =
              personalization.testTypes.hasTestCampaign && !hasOfferModAccess;

            return (
              <Grid item key={personalization.id}>
                <PersonalizationOverviewCard
                  personalization={personalization}
                  organization={organization.data.org}
                  formattedData={formattedData}
                  isDataLoading={personalizationTotalsData.isLoading}
                  isUsedInTest={isUsed}
                  isSaving={isSaving[index]}
                  subscriptionPlanGate={
                    !isAdmin && (shouldBlockOfferStart || shouldBlockPriceStart)
                  }
                  inactiveSubscriptionGate={!isAdmin && !isSubscriptionActive}
                  onToggle={() => {
                    handleEnableToggle(personalization, index);
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <NoPersonalizationsView />
      )}
    </Grid>
  );
};
